import { fetchService } from 'api';
import { Product, Cart, Conversion } from 'interfaces';
import { reaction } from '@/components/NewCart/Reaction';
import { ShippingAddress } from '@/components/Auction/ModalShipping';

interface ReactionResponse {
  id: number;
}

interface SendPayload {
  productId: string;
  reaction: reaction;
}

interface UpdatePayload {
  reaction: string;
}

export const sendReaction = async (cartId: string, payload: SendPayload) =>
  fetchService.post<ReactionResponse>(`shop/${cartId}/reactions`, payload).then(({ id }) => id);

export const updateReaction = async (reactionId: number, payload: UpdatePayload) =>
  fetchService.patch<ReactionResponse>(`shop/reactions/${reactionId}`, payload).then(({ id }) => id);

export const fetchCart = (creatorSlug: string, slug: string) =>
  fetchService.get<Cart>(`api/carts/cart?creatorSlug=${creatorSlug}&slug=${slug}`);

export const fetchProducts = (cartId: string) => fetchService.get<Product[]>(`api/products?cartId=${cartId}`);

export const fetchProduct = (productId: string) => fetchService.get<Product>(`api/products/${productId}`);

interface PlaceBid {
  auctionId: string;
  personalizedCartId: number;
  amount: number;
}

export const placeBid = (payload: PlaceBid) => fetchService.post(`api/auctions/bid`, payload);

interface PlaceAutoIncrement {
  auctionId: string;
  personalizedCartId: number;
  amount: number;
  maxAmount: number;
}

export const placeAutoIncrement = (payload: PlaceAutoIncrement) => fetchService.post(`api/auctions/auto-increment`, payload);

interface ClientSecretInfo {
  status: string;
  clientSecret: string;
  customerId: string;
  shippingAmount: number;
}

export const fetchClientSecret = (productId: string): Promise<ClientSecretInfo> =>
  fetchService.post<ClientSecretInfo>(`api/payments/session`, { productId });
export const createPaymentIntent = (bidAmount: number, paymentMethodId: string, auctionId: string, cartId: string, customerId: string) =>
  fetchService.post('api/payments/bid', { paymentMethodId, cartId, auctionId, bidAmount, customerId });
export const createShippingAddress = (cartId: string, auctionId: string, formData: ShippingAddress) =>
  fetchService.post(`api/shipping?cartId=${cartId}`, { auctionId, ...formData });
export const checkShippingAddress = (cartId: string, auctionId: string) => fetchService.get(`api/shipping/${auctionId}/${cartId}`);
export const getShippingAddress = (cartId: string, auctionId: string) => fetchService.get(`api/shipping/${auctionId}/${cartId}/details`);
export const checkBidHistory = (cartId: string, auctionId: string) => fetchService.get(`api/payments/bid/${auctionId}?cartId=${cartId}`);
export const updateBid = (cartId: string, auctionId: string, code: string, amount: number) =>
  fetchService.post(`api/payments/bid/${auctionId}?cartId=${cartId}`, { code, amount });
export const generateOtpCode = (productId: string, cartId: string) => fetchService.post(`api/otp/${productId}?cartId=${cartId}`);
export const verifyOtpCode = (productId: string, cartId: string, code: string) =>
  fetchService.post(`api/otp/${productId}/verify?cartId=${cartId}`, { code });
export const getNotifiedWithCode = (cartId: string, auctionId: string, code: string) =>
  fetchService.get(`api/carts/${cartId}/notify/${auctionId}?code=${code}`);
export const getNotified = (cartId: string, auctionId: string) => fetchService.post(`api/carts/${cartId}/notify/${auctionId}`);
export const subscribe = (creatorSlug: string, cartId: string, phoneNumber: string) =>
  fetchService.post<{ personalizedCartId: string }>('api/creators/subscribe', { creatorSlug, cartId, phoneNumber });
// todo: we are not allowing to check who is subscribed to the cart
export const subscriber = (phoneNumber: string, cartId: string) =>
  fetchService.get<{ personalizedCartId: string }>(`api/creators/subscriber/${cartId}/${phoneNumber}`);
export const createConversion = (payload: Conversion) => fetchService.post<{ url: string }>(`api/link/conversion`, payload);
